import {
  BiothreatSignalSource,
  useListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet,
  useListNwssEventsNwssEventsGet,
  type ListNwssAnomalyEventClustersNwssAnomalyEventClustersListGetParams,
  type NwssAnomalyEventCluster,
} from "@phc/common/gen/threatAnalyticsGen"
import { isAxiosError } from "axios"
import type {
  AlertCurationFilters,
  BiothreatSignal,
} from "./AlertCurationContext"
import { ALL_DISEASES } from "./AlertCurationUtils"
import { getDates, safelyParseDate } from "../../utils/dates"
import { useFlags } from "launchdarkly-react-client-sdk"
import { useMemo } from "react"

export const useNwssEventList = ({
  filters,
  showPrecomputedNwssAnomalyEvents,
}: {
  filters: AlertCurationFilters
  showPrecomputedNwssAnomalyEvents: boolean
}) => {
  const { dateRange } = filters
  const dates = getDates(dateRange)

  const response = useListNwssEventsNwssEventsGet(
    {
      disease:
        filters.disease && filters.disease !== ALL_DISEASES
          ? [filters.disease]
          : undefined,
      start_date: safelyParseDate(dates[0])?.format("YYYY-MM-DD"),
      end_date: safelyParseDate(dates[1])?.format("YYYY-MM-DD"),
      // this api doesn't support the following from alert candidates:
      // - sensitivity
      // - minScore
      // - search
      // - sessionId
    },
    {
      query: {
        enabled: !showPrecomputedNwssAnomalyEvents,
        retry: (failureCount, error) => {
          if (isAxiosError(error) && error.response?.status === 400) {
            return false
          }
          return failureCount < 3
        },
      },
    }
  )

  return response
}

export const useNwssAnomalyClusters = ({
  filters,
  showPrecomputedNwssAnomalyEvents,
}: {
  filters: AlertCurationFilters
  showPrecomputedNwssAnomalyEvents: boolean
}) => {
  const { dateRange } = filters
  const dates = getDates(dateRange)

  const params: ListNwssAnomalyEventClustersNwssAnomalyEventClustersListGetParams =
    {
      disease:
        filters.disease && filters.disease !== ALL_DISEASES
          ? [filters.disease]
          : undefined,
      event_start_date_start: safelyParseDate(dates[0])?.format("YYYY-MM-DD"),
      event_end_date_end: safelyParseDate(dates[1])?.format("YYYY-MM-DD"),
    }

  const response =
    useListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet(
      undefined,
      params,
      {
        query: {
          enabled: showPrecomputedNwssAnomalyEvents,
        },
      }
    )

  const transformedData = useMemo((): BiothreatSignal[] => {
    if (!response.data) return []

    const getLocationText = (cluster: NwssAnomalyEventCluster) => {
      if (cluster.event_geo_level === "state") {
        return cluster.state_territory.toUpperCase()
      }

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (cluster.event_geo_level === "county") {
        return `${cluster.geo_name}, ${cluster.state_territory.toUpperCase()}`
      }

      console.warn(`Unknown geo level for cluster ${cluster.id ?? "<unknown>"}`)
      return ""
    }

    const getSubLocatiions = (cluster: NwssAnomalyEventCluster) => {
      if (cluster.event_geo_level === "state") {
        return cluster.counties_names
      }

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (cluster.event_geo_level === "county") {
        // this should be the site ids but that is not ready yet
        return ""
      }

      return ""
    }

    return response.data.map(cluster => {
      return {
        id: cluster.id ?? cluster.event_id,
        source: BiothreatSignalSource.nwss,
        cluster_id: cluster.event_id,
        is_centroid: false,
        is_archived: false,
        cluster_count: cluster.anomaly_count,
        modern_score: null,
        legacy_score: null,
        title: cluster.description,
        url: "",
        publish_time: cluster.event_start_date,
        source_name: BiothreatSignalSource.nwss,
        pruned_themes: [],
        threat_category: cluster.disease,
        clean_disease_codes: cluster.disease ? [cluster.disease] : [],
        resolved_location: getLocationText(cluster),
        description: cluster.description,
        sub_locations: getSubLocatiions(cluster),
      }
    })
  }, [response.data])

  return {
    ...response,
    data: {
      count: transformedData.length,
      results: transformedData,
    },
  }
}

export const useNwssData = ({ filters }: { filters: AlertCurationFilters }) => {
  const { showPrecomputedNwssAnomalyEvents, showNwssBiothreatSignals } =
    useFlags<{
      showPrecomputedNwssAnomalyEvents?: boolean
      showNwssBiothreatSignals?: boolean
    }>()

  const events = useNwssEventList({
    filters,
    showPrecomputedNwssAnomalyEvents:
      !!showNwssBiothreatSignals && !showPrecomputedNwssAnomalyEvents,
  })
  const clusters = useNwssAnomalyClusters({
    filters,
    showPrecomputedNwssAnomalyEvents:
      !!showNwssBiothreatSignals && !!showPrecomputedNwssAnomalyEvents,
  })

  const data = showPrecomputedNwssAnomalyEvents
    ? clusters.data.results
    : (events.data?.results ?? [])

  const signals: BiothreatSignal[] = [...data]

  return {
    data: {
      count: signals.length,
      results: signals,
    },
    isLoading: events.isLoading || clusters.isLoading,
    isError: events.isError || clusters.isError,
  }
}
